import { useMemo, useCallback, useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { isOnline, generateTempId } from "@/lib/offlineSync";

export interface ScheduledSession {
  id: string;
  programId: string;
  programName?: string;
  week: number;
  workoutIndex: number;
  workoutName: string;
  scheduledDate: string;
  createdAt?: string;
}

const SCHEDULED_KEY = "myFitBeats_scheduled_sessions";
const PENDING_OPS_KEY = "myFitBeats_scheduled_pending_ops";
const TOMBSTONE_KEY = "myFitBeats_scheduled_tombstones";

interface PendingOp {
  id: string;
  type: 'create' | 'update' | 'delete';
  sessionId: string;
  payload: any;
  timestamp: number;
}

function getTombstones(): Set<string> {
  try {
    const stored = localStorage.getItem(TOMBSTONE_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {}
  return new Set();
}

function saveTombstones(tombstones: Set<string>) {
  localStorage.setItem(TOMBSTONE_KEY, JSON.stringify(Array.from(tombstones)));
}

function markCancelled(sessionId: string) {
  const tombstones = getTombstones();
  tombstones.add(sessionId);
  saveTombstones(tombstones);
}

function clearCancelledFor(sessionId: string) {
  const tombstones = getTombstones();
  tombstones.delete(sessionId);
  saveTombstones(tombstones);
}

function isCancelled(sessionId: string): boolean {
  return getTombstones().has(sessionId);
}

function getLocalCache(): ScheduledSession[] {
  try {
    const stored = localStorage.getItem(SCHEDULED_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      return data.sessions || [];
    }
  } catch {}
  return [];
}

function saveLocalCache(sessions: ScheduledSession[]) {
  localStorage.setItem(SCHEDULED_KEY, JSON.stringify({ sessions }));
}

function getPendingOps(): PendingOp[] {
  try {
    const stored = localStorage.getItem(PENDING_OPS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {}
  return [];
}

function savePendingOps(ops: PendingOp[]) {
  localStorage.setItem(PENDING_OPS_KEY, JSON.stringify(ops));
}

function addPendingCreateOp(session: ScheduledSession) {
  const ops = getPendingOps();
  const existingIdx = ops.findIndex(o => o.sessionId === session.id && o.type === 'create');
  
  if (existingIdx !== -1) {
    ops[existingIdx].payload = session;
    ops[existingIdx].timestamp = Date.now();
  } else {
    ops.push({
      id: generateTempId(),
      type: 'create',
      sessionId: session.id,
      payload: session,
      timestamp: Date.now(),
    });
  }
  savePendingOps(ops);
}

function addPendingUpdateOp(sessionId: string, updates: Partial<ScheduledSession>) {
  const ops = getPendingOps();
  
  const createIdx = ops.findIndex(o => o.sessionId === sessionId && o.type === 'create');
  if (createIdx !== -1) {
    ops[createIdx].payload = { ...ops[createIdx].payload, ...updates };
    ops[createIdx].timestamp = Date.now();
    savePendingOps(ops);
    return;
  }
  
  const updateIdx = ops.findIndex(o => o.sessionId === sessionId && o.type === 'update');
  if (updateIdx !== -1) {
    ops[updateIdx].payload = { ...ops[updateIdx].payload, ...updates };
    ops[updateIdx].timestamp = Date.now();
  } else {
    ops.push({
      id: generateTempId(),
      type: 'update',
      sessionId,
      payload: updates,
      timestamp: Date.now(),
    });
  }
  savePendingOps(ops);
}

function addPendingDeleteOp(sessionId: string) {
  markCancelled(sessionId);
  
  const ops = getPendingOps();
  
  const createIdx = ops.findIndex(o => o.sessionId === sessionId && o.type === 'create');
  if (createIdx !== -1) {
    ops.splice(createIdx, 1);
    savePendingOps(ops);
    return;
  }
  
  const updatedOps = ops.filter(o => o.sessionId !== sessionId);
  
  updatedOps.push({
    id: generateTempId(),
    type: 'delete',
    sessionId,
    payload: {},
    timestamp: Date.now(),
  });
  savePendingOps(updatedOps);
}

function removePendingOps(sessionId: string) {
  const ops = getPendingOps();
  savePendingOps(ops.filter(o => o.sessionId !== sessionId));
}

function replaceSessionId(oldId: string, newId: string) {
  const ops = getPendingOps();
  const updated = ops.map(o => 
    o.sessionId === oldId 
      ? { ...o, sessionId: newId, payload: o.payload?.id === oldId ? { ...o.payload, id: newId } : o.payload }
      : o
  );
  savePendingOps(updated);
  
  const cache = getLocalCache();
  const updatedCache = cache.map(s => s.id === oldId ? { ...s, id: newId } : s);
  saveLocalCache(updatedCache);
}

function applyOptimisticUpdates(sessions: ScheduledSession[] | null | undefined): ScheduledSession[] {
  const ops = getPendingOps();
  let result = [...(sessions || [])];
  
  const createOps = ops.filter(o => o.type === 'create');
  const existingIds = new Set(result.map(s => s.id));
  for (const op of createOps) {
    if (!existingIds.has(op.sessionId)) {
      result.push(op.payload);
    }
  }
  
  const updateOps = ops.filter(o => o.type === 'update');
  for (const op of updateOps) {
    result = result.map(s => s.id === op.sessionId ? { ...s, ...op.payload } : s);
  }
  
  const deleteOps = ops.filter(o => o.type === 'delete');
  const deleteIds = new Set(deleteOps.map(o => o.sessionId));
  result = result.filter(s => !deleteIds.has(s.id));
  
  return result;
}

export function useWorkoutSchedule() {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [localVersion, setLocalVersion] = useState(0);

  const { data: serverSchedule = [], isLoading, refetch, isFetched } = useQuery<ScheduledSession[]>({
    queryKey: ["/api/workout-schedule"],
    enabled: isAuthenticated,
    staleTime: 30000,
  });

  useEffect(() => {
    if (isFetched && isAuthenticated && isOnline()) {
      const ops = getPendingOps();
      if (ops.length === 0) {
        saveLocalCache(serverSchedule);
        setLocalVersion(v => v + 1);
      }
    }
  }, [serverSchedule, isFetched, isAuthenticated]);

  const sessions = useMemo(() => {
    const baseData = (isAuthenticated && isOnline() && isFetched) 
      ? serverSchedule 
      : getLocalCache();
    return applyOptimisticUpdates(baseData);
  }, [isAuthenticated, serverSchedule, isFetched, localVersion]);

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["/api/workout-schedule"] });
  }, [queryClient]);

  const triggerLocalUpdate = useCallback(() => {
    setLocalVersion(v => v + 1);
  }, []);

  const flushPendingOps = useCallback(async () => {
    if (!isAuthenticated || !isOnline()) return;
    
    const ops = getPendingOps();
    if (ops.length === 0) return;
    
    const sortedOps = [...ops].sort((a, b) => a.timestamp - b.timestamp);
    
    for (const op of sortedOps) {
      const currentOps = getPendingOps();
      const stillExists = currentOps.some(o => o.id === op.id);
      if (!stillExists) {
        continue;
      }
      
      if (isCancelled(op.sessionId) && op.type !== 'delete') {
        removePendingOps(op.sessionId);
        continue;
      }
      
      try {
        if (op.type === 'create') {
          const { id: tempId, ...sessionData } = op.payload;
          const response = await fetch("/api/workout-schedule", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(sessionData),
          });
          
          if (response.ok) {
            const serverSession = await response.json();
            
            if (isCancelled(tempId)) {
              try {
                const deleteResponse = await fetch(`/api/workout-schedule/${serverSession.id}`, {
                  method: "DELETE",
                  credentials: "include",
                });
                
                if (deleteResponse.ok || deleteResponse.status === 404) {
                  removePendingOps(tempId);
                  clearCancelledFor(tempId);
                } else {
                  markCancelled(serverSession.id);
                  clearCancelledFor(tempId);
                  addPendingDeleteOp(serverSession.id);
                  removePendingOps(tempId);
                }
              } catch (e) {
                markCancelled(serverSession.id);
                clearCancelledFor(tempId);
                addPendingDeleteOp(serverSession.id);
                removePendingOps(tempId);
              }
              continue;
            }
            
            replaceSessionId(tempId, serverSession.id);
            removePendingOps(serverSession.id);
            queryClient.setQueryData<ScheduledSession[]>(["/api/workout-schedule"], (old = []) => {
              const filtered = old.filter(s => s.id !== tempId);
              return [...filtered, serverSession];
            });
          }
        } else if (op.type === 'update') {
          if (isCancelled(op.sessionId)) {
            removePendingOps(op.sessionId);
            continue;
          }
          
          const response = await fetch(`/api/workout-schedule/${op.sessionId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(op.payload),
          });
          
          if (response.ok) {
            const serverSession = await response.json();
            removePendingOps(op.sessionId);
            queryClient.setQueryData<ScheduledSession[]>(["/api/workout-schedule"], (old = []) => 
              old.map(s => s.id === op.sessionId ? serverSession : s)
            );
          } else if (response.status === 404) {
            removePendingOps(op.sessionId);
          }
        } else if (op.type === 'delete') {
          const response = await fetch(`/api/workout-schedule/${op.sessionId}`, {
            method: "DELETE",
            credentials: "include",
          });
          
          if (response.ok || response.status === 404) {
            removePendingOps(op.sessionId);
            clearCancelledFor(op.sessionId);
            queryClient.setQueryData<ScheduledSession[]>(["/api/workout-schedule"], (old = []) => 
              old.filter(s => s.id !== op.sessionId)
            );
          }
        }
      } catch (e) {
        console.warn("Failed to flush pending op:", op.type, e);
      }
    }
    
    triggerLocalUpdate();
    invalidateQueries();
  }, [isAuthenticated, invalidateQueries, queryClient, triggerLocalUpdate]);

  useEffect(() => {
    const handleOnline = () => {
      if (isAuthenticated) {
        flushPendingOps();
      }
    };
    
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [isAuthenticated, flushPendingOps]);

  const createMutation = useMutation({
    mutationFn: async (session: Omit<ScheduledSession, "id" | "createdAt">) => {
      const tempId = generateTempId();
      const newSession: ScheduledSession = { ...session, id: tempId };
      
      const cache = getLocalCache();
      cache.push(newSession);
      saveLocalCache(cache);
      
      if (!isAuthenticated || !isOnline()) {
        addPendingCreateOp(newSession);
        triggerLocalUpdate();
        return newSession;
      }
      
      try {
        const response = await fetch("/api/workout-schedule", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(session),
        });
        
        if (response.ok) {
          const serverSession = await response.json();
          const updatedCache = getLocalCache().map(s => 
            s.id === tempId ? { ...serverSession } : s
          );
          saveLocalCache(updatedCache);
          queryClient.setQueryData<ScheduledSession[]>(["/api/workout-schedule"], (old = []) => {
            return [...old, serverSession];
          });
          triggerLocalUpdate();
          return serverSession;
        } else {
          addPendingCreateOp(newSession);
          triggerLocalUpdate();
          return newSession;
        }
      } catch (e) {
        console.warn("Offline: caching create", e);
        addPendingCreateOp(newSession);
        triggerLocalUpdate();
        return newSession;
      }
    },
    onSuccess: invalidateQueries,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, scheduledDate }: { id: string; scheduledDate: string }) => {
      const cache = getLocalCache();
      const updated = cache.map(s => s.id === id ? { ...s, scheduledDate } : s);
      saveLocalCache(updated);
      
      if (!isAuthenticated || !isOnline()) {
        addPendingUpdateOp(id, { scheduledDate });
        triggerLocalUpdate();
        return updated.find(s => s.id === id);
      }
      
      try {
        const response = await fetch(`/api/workout-schedule/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ scheduledDate }),
        });
        
        if (response.ok) {
          triggerLocalUpdate();
          return response.json();
        } else {
          addPendingUpdateOp(id, { scheduledDate });
          triggerLocalUpdate();
          return updated.find(s => s.id === id);
        }
      } catch (e) {
        console.warn("Offline: caching update", e);
        addPendingUpdateOp(id, { scheduledDate });
        triggerLocalUpdate();
        return updated.find(s => s.id === id);
      }
    },
    onSuccess: invalidateQueries,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const cache = getLocalCache();
      saveLocalCache(cache.filter(s => s.id !== id));
      
      const ops = getPendingOps();
      const hasPendingCreate = ops.some(o => o.sessionId === id && o.type === 'create');
      
      if (hasPendingCreate) {
        addPendingDeleteOp(id);
        triggerLocalUpdate();
        return;
      }
      
      if (!isAuthenticated || !isOnline()) {
        addPendingDeleteOp(id);
        triggerLocalUpdate();
        return;
      }
      
      try {
        const response = await fetch(`/api/workout-schedule/${id}`, {
          method: "DELETE",
          credentials: "include",
        });
        
        if (!response.ok && response.status !== 404) {
          addPendingDeleteOp(id);
        }
        queryClient.setQueryData<ScheduledSession[]>(["/api/workout-schedule"], (old = []) => 
          old.filter(s => s.id !== id)
        );
        triggerLocalUpdate();
      } catch (e) {
        console.warn("Offline: caching delete", e);
        addPendingDeleteOp(id);
        triggerLocalUpdate();
      }
    },
    onSuccess: invalidateQueries,
  });

  const deleteByProgram = useMutation({
    mutationFn: async (programId: string) => {
      const sessionsToDelete = sessions.filter(s => s.programId === programId);
      
      const cache = getLocalCache();
      saveLocalCache(cache.filter(s => s.programId !== programId));
      
      for (const session of sessionsToDelete) {
        if (!isAuthenticated || !isOnline()) {
          addPendingDeleteOp(session.id);
        } else {
          try {
            await fetch(`/api/workout-schedule/${session.id}`, {
              method: "DELETE",
              credentials: "include",
            });
          } catch (e) {
            addPendingDeleteOp(session.id);
          }
        }
      }
      triggerLocalUpdate();
    },
    onSuccess: invalidateQueries,
  });

  const syncFromLocal = useMutation({
    mutationFn: async () => {
      await flushPendingOps();
    },
    onSuccess: invalidateQueries,
  });

  return {
    sessions,
    isLoading,
    refetch,
    createSession: createMutation.mutateAsync,
    updateSession: updateMutation.mutateAsync,
    deleteSession: deleteMutation.mutateAsync,
    deleteByProgram: deleteByProgram.mutateAsync,
    syncFromLocal: syncFromLocal.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
